"use client"

import {
    animate,
    AnimationPlaybackControls,
    motion,
} from "motion/react"
import { ElementType, useEffect, useRef, useState, useCallback } from "react"
import { TypewriterProps, TYPING_SPEEDS } from "./types"

// Pause marker - use | in text to create a pause
const PAUSE_MARKER = "|"

export function Typewriter<T extends ElementType = "span">({
    children: text = "",
    as,
    speed = "normal",
    variance = 0,
    pauseDelay = 600,
    cursorClassName = "motion-typewriter-cursor",
    cursorStyle,
    cursorBlinkDuration = 0.5,
    onComplete,
    play = true,
    "aria-label": ariaLabel,
    textClassName,
    textStyle,
    ...props
}: TypewriterProps<T>) {
    const Component = (as || "span") as ElementType
    const [displayText, setDisplayText] = useState("")
    const cursorBlinkAnimation = useRef<AnimationPlaybackControls | null>(null)
    const cursorRef = useRef<HTMLSpanElement>(null)
    const timeoutRef = useRef<NodeJS.Timeout | null>(null)
    const completedRef = useRef(false)
    const indexRef = useRef(0)

    const baseInterval = typeof speed === "number" ? speed : TYPING_SPEEDS[speed]
    
    // Strip pause markers for display and aria-label
    const cleanText = text.replace(new RegExp(`\\${PAUSE_MARKER}`, 'g'), '')
    
    // Get typing delay with optional variance
    const getDelay = useCallback(() => {
        const varianceAmount = typeof variance === "number" ? variance / 100 : 0.25
        const randomFactor = 1 + (Math.random() - 0.5) * 2 * varianceAmount
        return baseInterval * randomFactor
    }, [baseInterval, variance])

    const startCursorBlinkAnimation = useCallback(() => {
        if (cursorRef.current) {
            cursorBlinkAnimation.current = animate(
                cursorRef.current,
                {
                    opacity: [1, 1, 0, 0],
                },
                {
                    duration: cursorBlinkDuration,
                    times: [0, 0.5, 0.5, 1],
                    ease: "linear",
                    repeat: Infinity,
                    repeatType: "reverse",
                }
            )
        }
    }, [cursorBlinkDuration])

    useEffect(() => {
        // Reset when text changes
        setDisplayText("")
        indexRef.current = 0
        completedRef.current = false
        cursorBlinkAnimation.current?.cancel()
    }, [text])

    useEffect(() => {
        if (!play) {
            startCursorBlinkAnimation()
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
                timeoutRef.current = null
            }
            return
        }

        cursorBlinkAnimation.current?.cancel()

        const typeNextChar = () => {
            const currentIndex = indexRef.current
            
            if (currentIndex >= text.length) {
                if (!completedRef.current) {
                    completedRef.current = true
                    startCursorBlinkAnimation()
                    onComplete?.()
                }
                return
            }
            
            const char = text[currentIndex]
            indexRef.current = currentIndex + 1
            
            // Check if this is a pause marker
            if (char === PAUSE_MARKER) {
                // Don't add to display, just wait longer
                timeoutRef.current = setTimeout(typeNextChar, pauseDelay)
            } else {
                // Add character to display
                setDisplayText(prev => prev + char)
                timeoutRef.current = setTimeout(typeNextChar, getDelay())
            }
        }

        // Start typing
        timeoutRef.current = setTimeout(typeNextChar, getDelay())

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
                timeoutRef.current = null
            }
        }
    }, [play, text, pauseDelay, getDelay, onComplete, startCursorBlinkAnimation])

    return (
        <Component {...props} aria-label={ariaLabel || cleanText}>
            <span className={textClassName} style={textStyle}>
                {displayText}
            </span>
            <motion.span
                ref={cursorRef}
                className={cursorClassName}
                style={{
                    display: "inline-block",
                    width: "2px",
                    height: "1em",
                    backgroundColor: "currentColor",
                    position: "relative",
                    top: "0.1em",
                    left: "0.2em",
                    ...cursorStyle,
                }}
            />
        </Component>
    )
}
